import cn from 'classnames';
import connect from 'components/Shared/connect';
import { SocialLink as SocialLinkModel } from 'models/SocialLink';
import { IconProps } from 'components/Shared/Icon';
import { IconFacebook } from 'components/Shared/Icon/themes/BGH-RD3/iconFacebook';
import { IconInstagram } from 'components/Shared/Icon/themes/BGH-RD3/iconInstagram';
import { IconLinkedin } from 'components/Shared/Icon/themes/BGH-RD3/iconLinkedin';
import { IconYoutube } from 'components/Shared/Icon/themes/BGH-RD3/iconYoutube';
import { IconPinterest } from 'components/Shared/Icon/themes/BGH-RD3/iconPinterest';
import Markdown from 'components/Shared/Markdown';
import { FooterGroup } from 'models/FooterGroup';
import { FooterLink as FooterLinkModel } from 'models/FooterLink';
import { RedFooterModel } from 'models/RedFooterModel';
import { ComponentType } from 'react';
import { hasElements } from 'utils';
import styles from './footerLinks.scss';
import { getRedFooter } from './selector';
import { SocialChannel } from 'models/SocialChannel';
import { GtmFooterNavClick } from 'store/googleTagManager/actions';
import { useDispatch } from 'react-redux';

const getAnchorProps = (
  isExternal: boolean
): Pick<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'target' | 'rel'> =>
  isExternal
    ? {
        target: '_blank',
        rel: 'noreferrer'
      }
    : {};

type FooterLinkProps = FooterLinkModel & {
  onClick: () => void;
};

const FooterLink = ({ link, displayName, isExternal, onClick }: FooterLinkProps) => {
  return (
    <a className={styles.link} href={link} onClick={onClick} {...getAnchorProps(isExternal)}>
      {displayName}
    </a>
  );
};

interface LinkGroupProps extends Omit<FooterGroup, 'systemName'> {
  className?: string;
}

const LinkGroup = ({ displayName, links, className }: LinkGroupProps) => {
  const reduxDispatch = useDispatch();

  const handleOnClick = (url: string) => {
    reduxDispatch(GtmFooterNavClick(url));
  };

  return (
    <div className={cn(styles.linkSection, className)}>
      <h4>{displayName}</h4>

      <ul>
        {links.map(link => (
          <li key={link.displayName}>
            <FooterLink onClick={() => handleOnClick(link.link)} {...link} />
          </li>
        ))}
      </ul>
    </div>
  );
};

const socialLinkIcons: Record<SocialChannel, ComponentType<IconProps> | null> = {
  [SocialChannel.Facebook]: IconFacebook,
  [SocialChannel.Youtube]: IconYoutube,
  [SocialChannel.Instagram]: IconInstagram,
  [SocialChannel.LinkedIn]: IconLinkedin,
  [SocialChannel.Pinterest]: IconPinterest,
  [SocialChannel.None]: null
};

const SocialLink = ({ socialChannel, url, title }: SocialLinkModel) => {
  const IconComponent = socialLinkIcons[socialChannel];

  return (
    <a className={styles.link} href={url} target="_blank" rel="noreferrer">
      <div className={styles.linkTitle}>
        <span>{title}</span>
      </div>
      {IconComponent && <IconComponent size="large" className={styles.socialIcon} />}
    </a>
  );
};

const SocialLinks = ({ socialLinks }: Pick<RedFooterModel, 'socialLinks'>) => (
  <ul className={styles.socialLinks}>
    {socialLinks.map(({ title, socialChannel, url }) => (
      <li key={title}>
        <SocialLink socialChannel={socialChannel} url={url} title={title} />
      </li>
    ))}
  </ul>
);

const _FooterLinks = (props: RedFooterModel) => (
  <div className={styles.footerLinksSection}>
    <div className={styles.base}>
      <div className={styles.linkColumns}>
        {props.allFooterGroups?.map(item => {
          return (
            <LinkGroup
              key={item.displayName}
              displayName={item.displayName}
              links={item.links}
              className={styles.group}
            />
          );
        })}
      </div>
      <div className={styles.socialCopyWrapper}>
        {hasElements(props.socialLinks) && <SocialLinks socialLinks={props.socialLinks} />}
        <Markdown {...props.copyright} className={styles.copyright} />
      </div>
    </div>
  </div>
);

export const FooterLinks = connect(getRedFooter)(_FooterLinks);
